import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CodeEditor from "@uiw/react-textarea-code-editor";
import Lottie from "react-lottie";
import design from "../../assets/lottiefiles/design.json";
import WindowsIcon from "../../assets/icons/windows";
import LinuxIcon from "../../assets/icons/linux";
import AppleIcon from "../../assets/icons/apple";
import AndroidIcon from "../../assets/icons/android";
import HardwareIcon from "../../assets/icons/hardware";
import WordIcon from "../../assets/icons/word";
import PowerPointIcon from "../../assets/icons/powerpoint";
import ExcelIcon from "../../assets/icons/excel";
import CIcon from "../../assets/icons/c";
import CPPIcon from "../../assets/icons/cpp";
import MachineLearningIcon from "../../assets/icons/machinelearning";
import CSharpIcon from "../../assets/icons/csharp";
import ComputerVisionIcon from "../../assets/icons/computervision";
import PythonIcon from "../../assets/icons/python";
import HtmlIcon from "../../assets/icons/html";
import CssIcon from "../../assets/icons/css";
import JsIcon from "../../assets/icons/js";
import DjangoIcon from "../../assets/icons/django";
import FlaskIcon from "../../assets/icons/flask";
import ReactIcon from "../../assets/icons/react";
import VSCodeIcon from "../../assets/icons/vscode";
import PycharmIcon from "../../assets/icons/pycharm";
import JupyterIcon from "../../assets/icons/jupyter";
import AnacondaIcon from "../../assets/icons/anaconda";
import ComputerApplicationIcon from "../../assets/icons/computerapplication";
import XDIcon from "../../assets/icons/xd";
import PhotoshopIcon from "../../assets/icons/photoshop";
import IllustratorIcon from "../../assets/icons/illustrator";
import AfterEffectsIcon from "../../assets/icons/aftereffects";
import PremiereProIcon from "../../assets/icons/premierepro";
import { size } from "../../utils";
import Carousel from "react-material-ui-carousel";
import PlayIcon from "../../assets/icons/play";
import PauseIcon from "../../assets/icons/pause";
import { COLORS } from "../../utils";

const DATA = {
  line_s: size({ p: 0.06 }),
  box_s: size({ p: 10, h: true }),
  Paper1: {
    CODING: {
      c: `#include <stdio.h>
int main()
{
    printf("Hello, World!");
    return 0;
}`,
      cpp: `using namespace std;
int main()
{
    cout<<"Hello, World!";
    return 0;
}`,
      java: `public class Main {
    public static void main(String[] args) {
        System.out.println("Hello, World!");
    }
}`,
      csharp: `using System;
class Hello {         
    static void Main(string[] args)
    {
        Console.WriteLine("Hello, World!");
    }
}`,
      python: `print("Hello, World!")`,
      javascript: `console.log("Hello, World!")`,
      kotlin: `fun main(args: Array<String>) {
    println("Hello, World!")
}`,
      go: `package main
import "fmt"
func main() {
    fmt.Println("Hello, World!")
}`,
      swift: `print("Hello, World!")`,
      dart: `void main() {
  print("Hello, World!");
}`,
    },
  },
  Paper2: {
    design_svg: "/assets/images/home/design.svg",
  },
  Paper3: {
    icons: [
      [
        WindowsIcon,
        LinuxIcon,
        AppleIcon,
        HardwareIcon,
        AndroidIcon,
        WordIcon,
        ExcelIcon,
        PowerPointIcon,
        CIcon,
        CPPIcon,
      ],
      [
        MachineLearningIcon,
        CSharpIcon,
        ComputerVisionIcon,
        PythonIcon,
        HtmlIcon,
        CssIcon,
        JsIcon,
        DjangoIcon,
        FlaskIcon,
        ReactIcon,
      ],
      [
        VSCodeIcon,
        PycharmIcon,
        JupyterIcon,
        AnacondaIcon,
        ComputerApplicationIcon,
        XDIcon,
        PhotoshopIcon,
        IllustratorIcon,
        AfterEffectsIcon,
        PremiereProIcon,
      ],
    ],
  },
};

function ToolBarIcon(props) {
  return (
    <Box
      sx={{
        width: size({ p: 1 }),
        height: size({ p: 1 }),
        backgroundColor: props.color,
        borderRadius: size({ p: 50 }),
        marginRight: size({ p: 0.3 }),
      }}
    />
  );
}

function Paper1() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const code_keys = Object.keys(DATA.Paper1.CODING);
  const [cc, setCC] = useState(0);
  const [ci, setCI] = useState(0);
  const [text, setText] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setCI(ci + 1);
      setText(DATA.Paper1.CODING[code_keys[cc]].slice(0, ci));
      if (ci === DATA.Paper1.CODING[code_keys[cc]].length) {
        setCI(0);
        setCC((prevCC) => (prevCC + 1) % code_keys.length); // Loop back to 0
        setText("");
      }
    }, 100); // Adjust typing speed here
    return () => clearTimeout(timer); // Cleanup on unmount or re-render
  }, [ci, cc, code_keys]); // Add all dependencies

  return (
    <Grid
      item
      container
      flexWrap={"nowrap"}
      direction={"column"}
      alignItems={"center"}
      justifyContent={"center"}
      alignContent={"center"}
      rowGap={size({ p: 2, h: true })}
      height={size({ p: 80, h: true })}
      mt={isMobile ? "-8rem" : ""}
    >
      <Grid item>
        <Typography
          sx={{
            fontFamily: "Archia",
            fontSize: isMobile ? size({ p: 3.2 }) : size({ p: 2.2 }),
            px: isMobile ? "20px" : "",
            fontWeight: 600,
            textAlign: "center",
            color: `${COLORS.C2}`,
          }}
        >
          {"Learn practical skills in <programming/>"} <br /> to build impactful
          applications and launch your tech career
        </Typography>
      </Grid>
      <Grid item width={isMobile ? size({ p: 70 }) : size({ p: 60 })}>
        <Typography
          sx={{
            fontFamily: "Archia",
            fontSize: isMobile ? size({ p: 2.5 }) : size({ p: 1 }),
            px: isMobile ? "-20px" : "",
            mt: isMobile ? "-10px" : "",
            fontWeight: 400,
            textAlign: "center",
            color: `${COLORS.C1}A0`,
          }}
        >
          Unlock the power of programming at Genesys Academy and embark on a
          journey to learn how to build essential skills that propel your career
          to new heights. Our courses are designed to empower you with hands-on
          expertise in programming for a dynamic and thriving future.
        </Typography>
      </Grid>
      <Grid
        item
        sx={{
          backgroundColor: COLORS.C1 + "F0",
          borderRadius: isMobile ? size({ p: 2 }) : size({ p: 1 }),
          marginTop: isMobile
            ? size({ p: 1, h: true })
            : size({ p: 4, h: true }),
        }}
      >
        <Box
          sx={{
            height: isMobile
              ? size({ p: 20, h: true })
              : size({ p: 35, h: true }),
            width: isMobile ? size({ p: 60 }) : size({ p: 32 }),
          }}
        >
          <Box
            display={"flex"}
            sx={{
              borderBottom: `${size({ p: 0.5, h: true })} solid #FFFFFF80`,
              padding: size({ p: 1 }),
            }}
          >
            <ToolBarIcon color={"#FB5E54"} />
            <ToolBarIcon color={"#FBBA2A"} />
            <ToolBarIcon color={"#22C03A"} />
          </Box>
          <Box
            sx={{
              backgroundColor: "#fde9ceff", //#ceeefdff
              height: isMobile ? "90%" : "82%",
              borderRadius: isMobile ? "0 0 10px 10px" : "0 0 20px 20px",
              border: "1px solid #002E3A",
              borderTop: "1px solid #ffffff23",
            }}
          >
            <CodeEditor
              value={text}
              language={code_keys[cc]}
              style={{
                fontSize: isMobile ? size({ p: 2 }) : size({ p: 1 }),
                paddingLeft: size({ p: 1 }),
                backgroundColor: "transparent",
                fontFamily: "Space Mono",
              }}
            />
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
}
function Paper2() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <Grid
      item
      container
      flexWrap={isMobile ? "wrap" : "nowrap"}
      alignItems={"center"}
      justifyContent={"center"}
      alignContent={"center"}
      columnGap={size({ p: 4 })}
      height={isMobile ? size({ p: 45, h: true }) : size({ p: 80, h: true })}
      rowGap={isMobile ? size({ p: 0, h: true }) : 0} // Added row gap for mobile
    >
      <Grid
        item
        height={isMobile ? size({ p: 30, h: true }) : size({ p: 84, h: true })}
      >
        {" "}
        {/* Adjusted height for mobile */}
        <Lottie
          width={isMobile ? size({ p: 60 }) : size({ p: 40 })}
          height={isMobile ? size({ p: 60 }) : size({ p: 40 })}
          options={{
            loop: true,
            autoplay: true,
            animationData: design,
          }}
        />
      </Grid>
      <Grid item width={isMobile ? size({ p: 80 }) : size({ p: 47 })}>
        {" "}
        {/* Adjusted width for mobile */}
        <Typography
          sx={{
            fontFamily: "Archia",
            fontSize: isMobile ? size({ p: 4 }) : size({ p: 4.5 }), // Adjusted font size for mobile
            fontWeight: 700,
            textAlign: isMobile ? "center" : "left", // Centered text for mobile
            textTransform: "uppercase",
            lineHeight: isMobile
              ? size({ p: 6, h: true })
              : size({ p: 10.5, h: true }), // Adjusted line height for mobile
            color: `${COLORS.C2}`,
          }}
        >
          Become a professional & certified
          <br />
          <Box
            component="span"
            sx={{
              backgroundImage: `url(${DATA.Paper2.design_svg})`,
              WebkitTextFillColor: "transparent",
              WebkitBackgroundClip: "text",
              backgroundRepeat: "repeat",
              fontSize: isMobile ? size({ p: 6 }) : size({ p: 6 }), // Adjusted font size for mobile
              lineHeight: isMobile
                ? size({ p: 5, h: true })
                : size({ p: 13.5, h: true }), // Adjusted line height for mobile
              WebkitTextStroke: `${size({ p: 0.1 })} ${COLORS.C1}A0`,
            }}
          >
            PROGRAMMER
            <br />
            AND GRAPHIC DESIGNER
            <br />
          </Box>
        </Typography>
      </Grid>
    </Grid>
  );
}
function Paper3() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Grid
      item
      container
      flexWrap={"nowrap"}
      direction={"column"}
      alignItems={"center"}
      justifyContent={"center"}
      alignContent={"center"}
      rowGap={size({ p: 2, h: true })}
      height={size({ p: 80, h: true })}
      mt={isMobile ? "-8rem" : ""}
    >
      <Grid item>
        <Typography
          sx={{
            fontFamily: "Archia",
            fontSize: isMobile ? size({ p: 3.2 }) : size({ p: 2.2 }), // Adjusted font size for mobile
            fontWeight: 600,
            textAlign: "center",
            color: `${COLORS.C2}`,
          }}
        >
          Institute of professional foundation courses
        </Typography>
      </Grid>
      <Grid item width={isMobile ? size({ p: 80 }) : size({ p: 60 })}>
        {" "}
        {/* Adjusted width for mobile */}
        <Typography
          sx={{
            fontFamily: "Archia",
            fontSize: isMobile ? size({ p: 2.5 }) : size({ p: 1 }), // Adjusted font size for mobile
            fontWeight: 400,
            textAlign: "center",
            color: `${COLORS.C1}A0`,
          }}
        >
          Unlock the power of programming at Genesys Academy and embark on a
          journey to learn how to build essential skills that propel your career
          to new heights. Our courses are designed to empower you with hands-on
          expertise in programming for a dynamic and thriving future.
        </Typography>
      </Grid>
      <Grid item alignItems={"center"} mt={size({ p: 4, h: true })}>
        {DATA.Paper3.icons.map((icons, i) => {
          return (
            <Grid
              item
              container
              alignItems={"center"}
              justifyContent={"center"}
              sx={{
                borderBottom:
                  i === 2 ? "none" : `${size({ p: 0.1 })} solid ${COLORS.C1}50`,
                flexWrap: "wrap", // Allow icons to wrap on mobile
              }}
            >
              {icons.map((icon, j) => {
                const Icon = icon;
                return (
                  <Grid
                    item
                    sx={{
                      padding: isMobile ? size({ p: 1.5 }) : size({ p: 2 }), // Adjusted padding for mobile
                      borderRight:
                        j === icons.length - 1 || (isMobile && j % 5 === 4)
                          ? "none" // New logic for mobile border
                          : `${size({ p: 0.1 })} solid ${COLORS.C1}50`,
                      borderBottom:
                        isMobile && i < 2 && j >= 5 && j < 9
                          ? `${size({ p: 0.1 })} solid ${COLORS.C1}50`
                          : "none", // Added bottom border for rows in mobile
                      width: isMobile ? "20%" : "auto", // Each icon takes 20% width on mobile (5 icons per row)
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <Icon
                      width={isMobile ? size({ p: 4 }) : size({ p: 2.5 })} // Adjusted icon size for mobile
                      height={isMobile ? size({ p: 4 }) : size({ p: 2.5 })} // Adjusted icon size for mobile
                      fill={COLORS.C2}
                    />
                  </Grid>
                );
              })}
            </Grid>
          );
        })}
      </Grid>
    </Grid>
  );
}

function IndicatorIcon(props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { play, setPlay, currentIndex, setCurrentIndex } = props;
  const Icon = {
    true: PauseIcon,
    false: PlayIcon,
  }[play];
  const dot_sx = {
    width: size({ p: 1.4, h: true }),
    height: size({ p: 1.4, h: true }),
    border: `${size({ p: 0.1 })} solid white`,
    cursor: "pointer",
    borderRadius: "50%",
    "&:hover": {
      backgroundColor: "white",
    },
  };
  return (
    <Grid
      item
      container
      flexWrap={"nowrap"}
      width={isMobile ? size({ p: 20 }) : size({ p: 7 })}
      height={size({ p: 4.5, h: true })}
      alignItems={"center"}
      justifyContent={"center"}
      alignContent={"center"}
      justifyItems={"center"}
      columnGap={isMobile ? size({ p: 1 }) : size({ p: 0.5 })} // Adjusted column gap for mobile
      sx={{
        backgroundColor: COLORS.C1 + "E0",
        borderRadius: isMobile ? size({ p: 3.5 }) : size({ p: 1 }),
        mt: isMobile ? "-10rem" : "",
      }}
    >
      <Grid item>
        <Box
          onClick={() => {
            setCurrentIndex(0);
          }}
          sx={{
            ...dot_sx,
            backgroundColor: currentIndex === 0 ? "white" : "transparent",
          }}
        />
      </Grid>
      <Grid item>
        <Box
          onClick={() => {
            setCurrentIndex(1);
          }}
          sx={{
            ...dot_sx,
            backgroundColor: currentIndex === 1 ? "white" : "transparent",
          }}
        />
      </Grid>
      <Grid item>
        <Box
          onClick={() => {
            setCurrentIndex(2);
          }}
          sx={{
            ...dot_sx,
            backgroundColor: currentIndex === 2 ? "white" : "transparent",
          }}
        />
      </Grid>
      <Grid item>
        <Box
          sx={{
            width: size({ p: 0.05 }),
            height: size({ p: 3, h: true }),
            backgroundColor: "white",
          }}
        />
      </Grid>
      <Grid
        item
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon
          onClick={() => {
            setPlay(!play);
          }}
          width={isMobile ? size({ p: 3.5 }) : size({ p: 1.2 })}
          height={isMobile ? size({ p: 3.5 }) : size({ p: 1.2 })}
          fill={"white"}
          style={{
            cursor: "pointer",
          }}
        />
      </Grid>
    </Grid>
  );
}

export default function Courses() {
    const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [play, setPlay] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-play logic for carousel
  useEffect(() => {
    let intervalId;
    if (play) {
      intervalId = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % 3); // Assuming 3 papers
      }, 5000); // Change slide every 5 seconds
    }
    return () => clearInterval(intervalId);
  }, [play]);

  return (
    <Grid id="courses-section"
      item
      container
      flexWrap={"nowrap"}
      direction={"column"}
      alignItems={"center"}
      justifyContent={"center"}
      rowGap={size({ p: 4, h: true })}
      sx={{
        width: size({ p: 100 }),
        height: isMobile ? size({ p: 53, h: true }) : size({ p: 92, h: true }), // Adjusted height for mobile
      }}
    >
      <Carousel
        indicators={false}
        index={currentIndex}
        navButtonsAlwaysVisible={false}
        navButtonsProps={{
          style: {
            backgroundColor: COLORS.C1,
          },
        }}
        autoPlay={false} // Disable default autoPlay from Carousel, use custom useEffect
        onChange={(index) => {
          setCurrentIndex(index);
        }}
        swipe={true} // Enable swipe on mobile
        animation="slide" // Smooth slide animation
        duration={800} // Animation duration
        sx={{
          width: size({ p: 100 }),
          height: isMobile
            ? size({ p: 90, h: true })
            : size({ p: 80, h: true }), // Adjusted height for mobile
        }}
      >
        <Paper1 />
        <Paper2 />
        <Paper3 />
      </Carousel>
      <Box sx={{
        display:'flex',
        alignItems:'center',
        columnGap: 4
      }}>
      <IndicatorIcon
        play={play}
        setPlay={setPlay}
        currentIndex={currentIndex}
        setCurrentIndex={setCurrentIndex}
      />
      {/* Button */}
      <Grid item>
        <Button
          variant="contained"
          onClick={() => navigate('/courses')}
          sx={{
            backgroundColor: COLORS.C2,
            fontFamily: "Archia",
            fontWeight: 500,
            color: COLORS.WHITE,
            fontSize: {
              xs: size({ p: 0.8 }),
              sm: size({ p: 0.9 }),
            },
            textTransform: "none",
            borderRadius: size({ p: 3 }),
            padding: {
              xs: "8px 16px",
              sm: "7px 20px",
            },
            ":hover": {
              backgroundColor: COLORS.C1,
            },
          }}
        >
          Explore Courses ↗
        </Button>
      </Grid>
      </Box>
    </Grid>
  );
}
